import { useMemo, useState } from "react";
import useSWR from "swr";
import dayjs from "dayjs";
import { Group, Title, Text, ThemeIcon, Tabs, SimpleGrid } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { IconChartBar, IconTag, IconShoppingCart, IconUsers } from "@tabler/icons-react";
import { fetcher } from "../../../services/fetcher";
import classes from "./BookingStats.module.css";

const STATS_ENDPOINT = "asc-booking/sales-booking-stats";
const EMPTY_VALUE = "--";

const formatApiDate = (date) => (date ? dayjs(date).format("YYYY-MM-DD") : null);

const toNumber = (value, fallback = 0) => {
  if (value === null || value === undefined || value === "") return fallback;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : fallback;
};

const formatNumber = (value, loading) =>
  loading ? EMPTY_VALUE : toNumber(value).toLocaleString("en-IN");

const buildStatsUrl = (fromDate, toDate) => {
  const params = new URLSearchParams();
  const startDate = formatApiDate(fromDate);
  const endDate = formatApiDate(toDate);

  if (startDate) {
    params.set("date_from", startDate);
  }
  if (endDate) {
    params.set("date_to", endDate);
  }

  const query = params.toString();
  return query ? `${STATS_ENDPOINT}?${query}` : STATS_ENDPOINT;
};

function BookingStats() {
  const today = useMemo(() => dayjs().startOf("day").toDate(), []);
  const [dateRange, setDateRange] = useState({
    from: today,
    to: today,
  });
  const [activeTab, setActiveTab] = useState("status");

  const statsUrl = useMemo(
    () => buildStatsUrl(dateRange.from, dateRange.to),
    [dateRange.from, dateRange.to]
  );

  const { data, isLoading } = useSWR(statsUrl, fetcher, {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  });

  const stats = data?.data || data || {};
  const overview = stats.overview || {};
  const bookingStatus = overview.booking_status || {};
  const bookingSource = stats.booking_source || {};
  const purchaseMethod = stats.purchase_method_breakdown || {};
  const topAgents = stats.top_5_agents || [];

  const totalBookings = overview.total_bookings || 0;
  const converted = overview.converted || 0;
  const pending = overview.pending || 0;
  const totalPax = overview.total_pax || 0;

  const handleDateChange = (field, value) => {
    setDateRange((current) => {
      const nextDate = value ? dayjs(value).startOf("day").toDate() : null;
      const next = { ...current, [field]: nextDate };

      if (next.from && next.to && dayjs(next.to).isBefore(dayjs(next.from), "day")) {
        if (field === "from") next.to = next.from;
        if (field === "to") next.from = next.to;
      }

      return next;
    });
  };

  const getStatusBgColor = (status) => {
    switch (status.toLowerCase()) {
      case "confirmed":
        return "#e8f5e9";
      case "pending":
        return "#fff3e0";
      case "pay_pending":
        return "#e3f2fd";
      case "cancelled":
        return "#ffebee";
      default:
        return "#f5f5f5";
    }
  };

  const getStatusTextColor = (status) => {
    switch (status.toLowerCase()) {
      case "confirmed":
        return "#2e7d32";
      case "pending":
        return "#e65100";
      case "pay_pending":
        return "#1565c0";
      case "cancelled":
        return "#c62828";
      default:
        return "#666";
    }
  };

  const statusEntries = Object.entries(bookingStatus);

  return (
    <section className={classes.statsShell}>
      <Group className={classes.header} justify="space-between" align="center" wrap="wrap" gap="md">
        <Group gap="sm" wrap="nowrap">
          <ThemeIcon size={30} radius="sm" variant="light" color="blue">
            <IconChartBar size={20} stroke={1.8} />
          </ThemeIcon>
          <Title fw="700" fz="h3" c="#001b3d">Booking stats</Title>
        </Group>

        <Group gap={8} wrap="wrap">
          <Text className={classes.dateLabel}>From</Text>
          <DateInput
            value={dateRange.from}
            onChange={(value) => handleDateChange("from", value)}
            valueFormat="DD-MM-YYYY"
            size="xs"
            maxDate={dateRange.to || today}
          />
          <Text className={classes.dateLabel}>To</Text>
          <DateInput
            value={dateRange.to}
            onChange={(value) => handleDateChange("to", value)}
            valueFormat="DD-MM-YYYY"
            size="xs"
            minDate={dateRange.from || undefined}
            maxDate={today}
          />
        </Group>
      </Group>

      <div className={classes.collapseContent}>
      <div className={classes.topMetricsRow}>
        <div className={classes.metricItem}>
          <div className={classes.metricDot} style={{ backgroundColor: "#228be6" }} />
          <div className={classes.metricContent}>
            <Text className={classes.metricNumber}>{formatNumber(totalBookings, isLoading)}</Text>
            <Text className={classes.metricName}>Total bookings</Text>
          </div>
        </div>

        <div className={classes.metricDivider} />

        <div className={classes.metricItem}>
          <div className={classes.metricDot} style={{ backgroundColor: "#16a34a" }} />
          <div className={classes.metricContent}>
            <Text className={classes.metricNumber}>{formatNumber(converted, isLoading)}</Text>
            <Text className={classes.metricName}>Converted</Text>
          </div>
        </div>

        <div className={classes.metricDivider} />

        <div className={classes.metricItem}>
          <div className={classes.metricDot} style={{ backgroundColor: "#f97316" }} />
          <div className={classes.metricContent}>
            <Text className={classes.metricNumber}>{formatNumber(pending, isLoading)}</Text>
            <Text className={classes.metricName}>Pending</Text>
          </div>
        </div>

        <div className={classes.metricDivider} />

        <div className={classes.metricItem}>
          <div className={classes.metricDot} style={{ backgroundColor: "#00FF00" }} />
          <div className={classes.metricContent}>
            <Text className={classes.metricNumber}>{formatNumber(totalPax, isLoading)}</Text>
            <Text className={classes.metricName}>Total Pax</Text>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className={classes.tabNav}>
        <Tabs
          value={activeTab}
          onChange={setActiveTab}
          variant="pills"
          color="gray"
          className={classes.tabs}
        >
          <Tabs.List>
            <Tabs.Tab value="status" leftSection={<IconTag size={16} />}>
              Status
            </Tabs.Tab>
            <Tabs.Tab value="source" leftSection={<IconChartBar size={16} />}>
              Source
            </Tabs.Tab>
            <Tabs.Tab value="purchase" leftSection={<IconShoppingCart size={16} />}>
              Purchase
            </Tabs.Tab>
            <Tabs.Tab value="agents" leftSection={<IconUsers size={16} />}>
              Agents
            </Tabs.Tab>
          </Tabs.List>
        </Tabs>
      </div>

      {/* Tab Content */}
      {activeTab === "status" && (
        <div className={classes.statusSection}>
          <SimpleGrid cols={{ base: 2, xs: 4 }} spacing="sm">
            {statusEntries.map(([status, count]) => (
              <div
                key={status}
                className={classes.statusCard}
                style={{
                  backgroundColor: getStatusBgColor(status),
                  borderColor: getStatusTextColor(status),
                }}
              >
                <Text className={classes.statusCardValue} style={{ color: getStatusTextColor(status) }}>
                  {formatNumber(count, isLoading)}
                </Text>
                <Text className={classes.statusCardLabel} style={{ color: getStatusTextColor(status) }}>
                  {status.replace(/_/g, " ")}
                </Text>
              </div>
            ))}
          </SimpleGrid>
        </div>
      )}

      {activeTab === "source" && Object.keys(bookingSource).length > 0 && (
        <div className={classes.section}>
          <Text className={classes.sectionTitle}>Booking Source</Text>
          <SimpleGrid cols={{ base: 2, xs: 3, md: 5 }} spacing="sm">
            {Object.entries(bookingSource).map(([source, count]) => (
              <div key={source} className={classes.sourceItem}>
                <Text className={classes.sourceLabel}>{source.replace(/_/g, " ")}</Text>
                <Text className={classes.sourceValue}>{formatNumber(count, isLoading)}</Text>
              </div>
            ))}
          </SimpleGrid>
        </div>
      )}

      {activeTab === "purchase" && Object.keys(purchaseMethod).length > 0 && (
        <div className={classes.section}>
          <Text className={classes.sectionTitle}>Purchase Method Breakdown</Text>
          <div className={classes.tableWrapper}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th>Method</th>
                  <th>Total</th>
                  <th>Converted</th>
                  <th>Pending</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(purchaseMethod).map(([method, data]) => (
                  <tr key={method}>
                    <td>{method.replace(/_/g, " ")}</td>
                    <td>{formatNumber(data.total, isLoading)}</td>
                    <td>{formatNumber(data.converted, isLoading)}</td>
                    <td>{formatNumber(data.pending, isLoading)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === "agents" && topAgents.length > 0 && (
        <div className={classes.section}>
          <Text className={classes.sectionTitle}>Top 5 Agents</Text>
          <div className={classes.tableWrapper}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th>Agent Name</th>
                  <th>Booking Count</th>
                  <th>Converted</th>
                  <th>Pending</th>
                </tr>
              </thead>
              <tbody>
                {topAgents.map((agent) => (
                  <tr key={agent.agent_id}>
                    <td>{agent.agent_name}</td>
                    <td>{formatNumber(agent.booking_count, isLoading)}</td>
                    <td>{formatNumber(agent.converted, isLoading)}</td>
                    <td>{formatNumber(agent.pending, isLoading)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      </div>
    </section>
  );
}

export default BookingStats;